import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { MobileMenu } from '@/components/layout/MobileMenu';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { brandLang, useI18n, type MessageKey } from '@/i18n';
import { categoryLabel, HEADER_CATEGORIES } from '@/lib/categories';
import type { HubCategory } from '@/types';

interface TopNavProps {
  categories: HubCategory[];
  activeCategory?: string;
  onCategoryChange?: (categoryId: string) => void;
}

const SECTION_LINKS: Array<{ to: string; key: MessageKey }> = [
  { to: '/roadmap', key: 'nav.roadmap' },
  { to: '/assets', key: 'nav.assets' },
];

export function TopNav({ categories, activeCategory, onCategoryChange }: TopNavProps) {
  const { email, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const onLibrary = location.pathname.startsWith('/library');

  function handleCategoryClick(categoryId: string) {
    onCategoryChange?.(categoryId);
    navigate(`/library?category=${encodeURIComponent(categoryId)}`);
  }

  function closeProductsMenu(target: HTMLElement) {
    target.closest('details')?.removeAttribute('open');
  }

  return (
    <header className="top-nav glass-panel" data-hero-chrome>
      <div className="top-nav__inner site-pad-x">
        <Link to="/" className="top-nav__brand" aria-label={t('nav.brandAria')}>
          <img
            src="/brand/richyplay-logo.png"
            alt="RichyPlay"
            className="top-nav__logo"
            width={180}
            height={32}
          />
          <span className="top-nav__brand-divider" aria-hidden="true" />
          <span className="top-nav__brand-hub" lang={brandLang(t('nav.clientHub'))}>
            {t('nav.clientHub')}
          </span>
        </Link>

        <nav className="top-nav__categories" aria-label={t('nav.categoriesAria')}>
          {HEADER_CATEGORIES.map((item) => {
            const meta = categories.find((cat) => cat.id === item.id);
            /* A category the hub does not serve yet is announced, not linked. */
            if (!meta || meta.comingSoon) {
              return (
                <span key={item.id} className="nav-link nav-link--soon is-disabled" aria-disabled="true">
                  <span>{t(item.key)}</span>
                  <span className="nav-link__soon">{t('nav.comingSoon')}</span>
                </span>
              );
            }
            return (
              <Link
                key={item.id}
                to={`/library?category=${encodeURIComponent(item.id)}`}
                className={`nav-link${onLibrary && activeCategory === item.id ? ' is-active' : ''}`}
                onClick={() => onCategoryChange?.(item.id)}
              >
                {t(item.key)}
              </Link>
            );
          })}
        </nav>

        {categories.length > 0 ? (
          <details className="top-nav__products nav-dropdown">
            <summary className="nav-link">
              <span>{t('nav.products')}</span>
              <svg className="nav-link__caret" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </summary>
            <div className="nav-dropdown__panel">
              <div className="nav-dropdown__menu" aria-label={t('nav.categoriesAria')}>
                {categories.map((cat) => {
                  const label = categoryLabel(t, cat);
                  return cat.comingSoon ? (
                    <div
                      key={cat.id}
                      className="nav-dropdown__item is-disabled"
                      aria-disabled="true"
                    >
                      <span>{label}</span>
                      <span className="nav-dropdown__soon">{t('nav.comingSoon')}</span>
                    </div>
                  ) : (
                    <button
                      key={cat.id}
                      type="button"
                      className={`nav-dropdown__item${onLibrary && activeCategory === cat.id ? ' is-selected' : ''}`}
                      onClick={(event) => {
                        closeProductsMenu(event.currentTarget);
                        handleCategoryClick(cat.id);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </details>
        ) : null}

        <nav className="top-nav__sections" aria-label={t('nav.sectionsAria')}>
          {SECTION_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
            >
              {t(link.key)}
            </NavLink>
          ))}
        </nav>

        <div className="top-nav__account nav-dropdown">
          <button
            type="button"
            className="nav-link account-trigger"
            aria-haspopup="true"
            aria-label={email ?? t('nav.account')}
          >
            <span className="account-trigger__avatar" aria-hidden="true">
              {(email?.[0] ?? 'U').toUpperCase()}
            </span>
          </button>
          <div className="nav-dropdown__panel">
            <div className="nav-dropdown__menu">
              <Link to="/account" className="nav-dropdown__item">
                {t('nav.account')}
              </Link>
              <button type="button" className="nav-dropdown__item" onClick={() => signOut()}>
                {t('nav.signOut')}
              </button>
              {email ? <p className="account-menu__email">{email}</p> : null}
            </div>
          </div>
        </div>

        <LanguageSelector />

        <MobileMenu categories={categories} onCategoryChange={onCategoryChange} />
      </div>
    </header>
  );
}
